import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = getArticleBySlug(slug || '');
  if (!article) {
    return (
      <div className="pt-24 pb-16 px-4 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-2xl font-bold text-foreground-900 mb-2">{t('page_not_found')}</h2>
        <p className="text-foreground-500 mb-6">{t('page_not_found_desc')}</p>
        <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
          {t('back_to_home')}
        </Link>
      </div>
    );
  }

  const category = getCategoryBySlug(article.category);
  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '麻豆传媒',
    },
  };

  const faqSchema =
    article.category === 'faq'
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: extractFAQs(article.content).map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a.substring(0, 300),
            },
          })),
        }
      : null;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}

      {/* Article Header */}
      <section className="relative w-full h-[300px] md:h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/30 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-1.5 text-white/80 text-sm mb-3 hover:text-white transition-colors cursor-pointer"
              >
                <i className={`${category.icon} text-sm`}></i>
                {category.name}
              </Link>
            )}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-white/70 text-sm flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views > 10000
                  ? `${(article.views / 10000).toFixed(1)}万 阅读`
                  : `${article.views} 阅读`}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="w-full px-4 md:px-6 pt-6">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: t('breadcrumb_home'), path: '/' },
              ...(category
                ? [{ label: category.name, path: `/category/${category.slug}` }]
                : []),
              { label: article.title },
            ]}
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="py-8 md:py-12 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div
                  className="prose prose-lg max-w-none
                    prose-headings:text-foreground-900 prose-headings:font-heading prose-headings:font-bold
                    prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                    prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                    prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                    prose-li:text-foreground-700 prose-li:leading-relaxed
                    prose-strong:text-foreground-900
                    prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                    prose-table:border prose-table:border-background-200
                    prose-th:bg-background-100 prose-th:text-foreground-800 prose-th:px-4 prose-th:py-2
                    prose-td:px-4 prose-td:py-2 prose-td:border-background-100
                  "
                  dangerouslySetInnerHTML={{ __html: article.content }}
                ></div>

                {/* Tags */}
                <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-[300px] shrink-0">
              <div className="lg:sticky lg:top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-3-line text-primary-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{article.author}</p>
                      <p className="text-xs text-foreground-500">官方认证作者</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-article-line"></i>
                      12 篇文章
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views > 10000
                        ? `${(article.views / 10000).toFixed(1)}万`
                        : article.views}
                    </span>
                  </div>
                </div>

                {/* Related Articles */}
                {relatedArticles.length > 0 && (
                  <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-4 font-heading">
                      {t('related_articles')}
                    </h3>
                    <div className="space-y-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.slug}
                          to={`/article/${ra.slug}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0">
                            <img
                              src={ra.image}
                              alt={ra.title}
                              className="w-full h-full object-cover object-top"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground-800 font-medium line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {ra.title}
                            </p>
                            <span className="text-xs text-foreground-400 mt-1 block">
                              {ra.date}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Links */}
                <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4 font-heading">
                    快速导航
                  </h3>
                  <div className="space-y-2">
                    {categories.slice(0, 5).map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1 cursor-pointer"
                      >
                        <i className={`${cat.icon} text-xs`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}

function extractFAQs(content: string): { q: string; a: string }[] {
  const faqs: { q: string; a: string }[] = [];
  const qRegex = /<h3>Q[：:](.*?)<\/h3>/g;
  const aRegex = /<p>A[：:]([\s\S]*?)<\/p>/g;

  const qMatches = [...content.matchAll(qRegex)];
  const aMatches = [...content.matchAll(aRegex)];

  qMatches.forEach((qMatch, i) => {
    if (aMatches[i]) {
      faqs.push({
        q: qMatch[1].trim(),
        a: aMatches[i][1].replace(/<[^>]+>/g, '').trim(),
      });
    }
  });

  return faqs;
}